import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildMonthGrid, toISO } from "@/lib/calendar";
import { mget, fmtDay, todayISO, EVENT_STATUS } from "@/manager/lib";
import { PageHeader, Card, KindPill, StatusPill, goldBtn } from "@/manager/ui";
import EventFormDialog from "@/manager/components/EventFormDialog";

const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const chip = (e) => (e.status === "cancelled" ? "bg-rose-50 text-rose-400 line-through" : e.kind === "sala" ? "bg-[#101B28] text-[#F7F5F0]" : "bg-[#BBA77D] text-[#101B28]")
  + (e.status === "provisional" ? " ring-1 ring-dashed ring-amber-500 opacity-80" : "");

const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const mondayOf = (iso) => { const d = new Date(`${iso}T12:00:00`); return addDays(iso, -((d.getDay() + 6) % 7)); };

function EventRow({ e, onClick }) {
  return (
    <button onClick={onClick} className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-[#e6e1d6] bg-white hover:shadow-sm" data-testid={`agenda-row-${e.id}`}>
      <span className="text-xs text-[#6b6a66] w-24">{e.start_time || "--:--"}–{e.end_time || "--:--"}</span>
      <span className="flex-1 text-sm font-medium truncate">{e.title}</span>
      <span className="text-xs text-[#6b6a66]">{e.guests ?? "?"} pers.</span>
      <KindPill kind={e.kind} /><StatusPill status={e.status} />
    </button>
  );
}

export default function Agenda() {
  const navigate = useNavigate();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(todayISO());
  const [kind, setKind] = useState("all");
  const [showCancelled, setShowCancelled] = useState(false);
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState(false);

  const range = useMemo(() => {
    if (view === "day") return [cursor, cursor];
    if (view === "week") { const m = mondayOf(cursor); return [m, addDays(m, 6)]; }
    const [y, mo] = cursor.split("-").map(Number);
    return [`${cursor.slice(0, 7)}-01`, toISO(y, mo - 1, new Date(y, mo, 0).getDate())];
  }, [view, cursor]);

  useEffect(() => { mget("/events", { start: range[0], end: range[1] }).then(setEvents); }, [range]);

  const visible = events.filter((e) => (kind === "all" || e.kind === kind) && (showCancelled || e.status !== "cancelled"));
  const byDate = visible.reduce((a, e) => ({ ...a, [e.date]: [...(a[e.date] || []), e] }), {});
  const step = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(addDays(cursor, 7 * dir));
    else { const [y, m] = cursor.split("-").map(Number); const d = new Date(y, m - 1 + dir, 1); setCursor(toISO(d.getFullYear(), d.getMonth(), 1)); }
  };
  const [y, m] = cursor.split("-").map(Number);
  const title = view === "month" ? `${MONTHS[m - 1]} ${y}` : view === "week" ? `Semana del ${fmtDay(range[0])}` : fmtDay(cursor);
  const open = (e) => navigate(`/manager/eventos/${e.id}`);

  return (
    <div data-testid="manager-agenda">
      <PageHeader eyebrow="Agenda" title="Calendario de Aura">
        <Button onClick={() => setNewEvent(true)} className={goldBtn} data-testid="agenda-new-event"><Plus size={15} /> Evento</Button>
      </PageHeader>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex bg-white border border-[#e6e1d6] rounded-full p-1">
          {[["month", "Mes"], ["week", "Semana"], ["day", "Día"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`agenda-view-${k}`} className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] ${view === k ? "bg-[#101B28] text-white" : "text-[#6b6a66]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex bg-white border border-[#e6e1d6] rounded-full p-1">
          {[["all", "Todo"], ["sala", "Sala"], ["externo", "Externos"]].map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`agenda-kind-${k}`} className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] ${kind === k ? "bg-[#BBA77D] text-[#101B28]" : "text-[#6b6a66]"}`}>{l}</button>
          ))}
        </div>
        <label className="text-xs text-[#6b6a66] flex items-center gap-2"><input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} data-testid="agenda-show-cancelled" /> Ver cancelados</label>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => step(-1)} className="w-9 h-9 rounded-full border border-[#e6e1d6] bg-white flex items-center justify-center" data-testid="agenda-prev"><ChevronLeft size={16} /></button>
          <span className="font-display text-xl min-w-[180px] text-center capitalize" data-testid="agenda-title">{title}</span>
          <button onClick={() => step(1)} className="w-9 h-9 rounded-full border border-[#e6e1d6] bg-white flex items-center justify-center" data-testid="agenda-next"><ChevronRight size={16} /></button>
          <Button variant="outline" className="rounded-full" onClick={() => setCursor(todayISO())} data-testid="agenda-today">Hoy</Button>
        </div>
      </div>

      {view === "month" ? (
        <Card className="p-3 md:p-4">
          <div className="grid grid-cols-7 gap-1 mb-1">{DOW.map((d) => <div key={d} className="text-center text-[10px] uppercase tracking-[0.16em] text-[#6b6a66]">{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {buildMonthGrid(y, m - 1).map((day, i) => {
              if (!day) return <div key={i} />;
              const iso = toISO(y, m - 1, day);
              const evs = byDate[iso] || [];
              return (
                <div key={iso} onClick={() => { setCursor(iso); setView("day"); }} data-testid={`agenda-day-${iso}`}
                  className={`min-h-[92px] rounded-lg border p-1.5 cursor-pointer hover:border-[#BBA77D] ${iso === todayISO() ? "border-[#BBA77D] bg-[#BBA77D]/10" : "border-[#eee9df]"}`}>
                  <span className="text-xs text-[#6b6a66]">{day}</span>
                  {evs.slice(0, 3).map((e) => (
                    <button key={e.id} onClick={(ev) => { ev.stopPropagation(); open(e); }} className={`block w-full text-left truncate text-[10px] px-1.5 py-0.5 rounded mt-0.5 ${chip(e)}`} data-testid={`agenda-chip-${e.id}`}>
                      {e.start_time && `${e.start_time} `}{e.title}
                    </button>
                  ))}
                  {evs.length > 3 && <span className="text-[10px] text-[#6b6a66]">+{evs.length - 3} más</span>}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-4 text-[10px] uppercase tracking-[0.14em] text-[#6b6a66]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#101B28]" />Sala</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#BBA77D]" />Externo</span>
            {Object.values(EVENT_STATUS).map((s) => <span key={s.label} className={`px-2 rounded-full border ${s.cls}`}>{s.label}</span>)}
          </div>
        </Card>
      ) : (
        <div className="space-y-5">
          {Array.from({ length: view === "week" ? 7 : 1 }, (_, i) => addDays(range[0], i)).map((iso) => (
            <div key={iso}>
              <p className="text-xs uppercase tracking-[0.16em] text-[#6b6a66] mb-2 capitalize">{fmtDay(iso)}</p>
              <div className="space-y-2">
                {(byDate[iso] || []).length ? byDate[iso].map((e) => <EventRow key={e.id} e={e} onClick={() => open(e)} />)
                  : <p className="text-sm text-[#b0aca3]">Sin eventos</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      <EventFormDialog open={newEvent} onClose={() => setNewEvent(false)} onSaved={(ev) => navigate(`/manager/eventos/${ev.id}`)} />
    </div>
  );
}
